import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Palmtree, Paperclip, Plus, Stethoscope, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtApiError, fmtFecha } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hoyISOBA } from "@/components/ClockBA";
import { Card, LICENCIAS, Modal, PageHeader, Badge, btnPrim, btnSec, inputCls } from "@/components/rrhh/ui";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const CELDA = {
  trabajo: "bg-[#2DD4BF]/20 text-[#2DD4BF]",
  falta: "bg-red-400/20 text-red-300",
  vacaciones: "bg-sky-400/20 text-sky-300",
  licencia_cert: "bg-fuchsia-400/20 text-fuchsia-300",
  licencia_sin: "bg-fuchsia-400/10 text-fuchsia-300/60",
  justificada: "bg-emerald-500/25 text-emerald-300",
  feriado: "bg-white/15 text-gray-300",
  franco: "bg-white/[0.04] text-transparent",
  futuro: "text-transparent",
  sin_dato: "text-transparent",
  fuera_alta: "text-transparent",
};
const LETRA = { falta: "F", vacaciones: "V", licencia_cert: "L", licencia_sin: "L", justificada: "J" };
const RAYADO = { backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,0.06) 0 2px, transparent 2px 5px)" };
const LEYENDA = [
  ["Vacaciones", "bg-sky-400/20"], ["Licencia con certificado", "bg-fuchsia-400/20"],
  ["Licencia sin certificado", "bg-fuchsia-400/10"], ["Falta", "bg-red-400/20"],
  ["Justificada", "bg-emerald-500/25"], ["Feriado", "bg-white/15"],
  ["Franco", "bg-white/[0.04]"], ["Trabajó", "bg-[#2DD4BF]/20"],
];

export default function CalendarioPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const esLiq = user?.rol === "liquidacion"; // solo lectura, salvo feriados
  const [mes, setMes] = useState(hoyISOBA().slice(0, 7));
  const [fLocal, setFLocal] = useState("");
  const [anio, setAnio] = useState(hoyISOBA().slice(0, 4));
  const [modal, setModal] = useState(null); // feriado | vacaciones | licencia | celda | evento
  const [inicial, setInicial] = useState(null); // { empleado_id, fecha }
  const [celdaSel, setCeldaSel] = useState(null); // { empleado, fecha }
  const [eventoSel, setEventoSel] = useState(null); // { evento, empleado }
  const [borrarFeriado, setBorrarFeriado] = useState(null);

  const { data: locales } = useQuery({ queryKey: ["locales"], queryFn: async () => (await api.get("/locales")).data });
  const { data: empleados } = useQuery({ queryKey: ["empleados"], queryFn: async () => (await api.get("/empleados")).data });
  const { data: grilla } = useQuery({
    queryKey: ["calendario-grilla", mes, fLocal],
    queryFn: async () => (await api.get("/rrhh/calendario/grilla", { params: { mes, local_id: fLocal || undefined } })).data,
  });
  const { data: feriados } = useQuery({
    queryKey: ["feriados", anio],
    queryFn: async () => (await api.get("/rrhh/feriados", { params: { anio } })).data,
  });

  const [y, m] = mes.split("-").map(Number);
  const moverMes = (delta) => {
    const d = new Date(y, m - 1 + delta, 1);
    setMes(iso(d).slice(0, 7));
  };

  const grupos = useMemo(() => {
    const porLocal = new Map();
    for (const e of grilla?.empleados || []) {
      if (!porLocal.has(e.local_id)) porLocal.set(e.local_id, { nombre: e.local_nombre, filas: [] });
      porLocal.get(e.local_id).filas.push(e);
    }
    const orden = (locales || []).map((l) => l.id);
    return [...porLocal.entries()].sort((a, b) => orden.indexOf(a[0]) - orden.indexOf(b[0]));
  }, [grilla, locales]);

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["calendario-grilla"] });
    qc.invalidateQueries({ queryKey: ["feriados"] });
    qc.invalidateQueries({ queryKey: ["calendario"] });
  };

  const tocarCelda = (emp, fecha) => {
    const ev = emp.eventos.find((e) => e.fecha_desde <= fecha && e.fecha_hasta >= fecha);
    if (ev) setEventoSel({ evento: ev, empleado: emp });
    else if (!esLiq) setCeldaSel({ empleado: emp, fecha });
  };

  const mutBorrarFeriado = useMutation({
    mutationFn: async (id) => api.delete(`/rrhh/calendario/${id}`),
    onSuccess: () => { setBorrarFeriado(null); invalidar(); toast.success("Feriado eliminado — las jornadas se recalcularon"); },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-calendario">
      <PageHeader
        titulo="Calendario"
        subtitulo="Las vacaciones, las licencias, las faltas y los feriados de cada persona, día por día."
        testid="header-calendario"
      >
        {!esLiq && (
          <>
            <button className={`${btnSec} flex items-center gap-2`} onClick={() => { setInicial(null); setModal("vacaciones"); }} data-testid="btn-cargar-vacaciones">
              <Palmtree size={14} /> Cargar vacaciones
            </button>
            <button className={`${btnPrim} flex items-center gap-2`} onClick={() => { setInicial(null); setModal("licencia"); }} data-testid="btn-cargar-licencia">
              <Stethoscope size={14} /> Cargar licencia
            </button>
          </>
        )}
      </PageHeader>

      <Card
        testid="card-grilla"
        bodyCls="p-0"
        titulo={`${MESES[m - 1][0].toUpperCase() + MESES[m - 1].slice(1)} de ${y}`}
        subtitulo="Una fila por persona, una columna por día"
        right={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <button onClick={() => moverMes(-1)} className="rounded-lg border border-white/10 p-1.5 text-gray-400 hover:text-white" data-testid="mes-anterior"><ChevronLeft size={15} /></button>
              <button onClick={() => moverMes(1)} className="rounded-lg border border-white/10 p-1.5 text-gray-400 hover:text-white" data-testid="mes-siguiente"><ChevronRight size={15} /></button>
            </div>
            <select value={fLocal} onChange={(e) => setFLocal(e.target.value)} className={inputCls} data-testid="filtro-local-cal">
              <option value="">Todos los locales</option>
              {locales?.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="border-collapse text-xs">
            <thead>
              <tr className="border-b border-white/8">
                <th className="sticky left-0 z-10 min-w-44 bg-[#151A21] px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">Persona</th>
                {grilla?.dias.map((d) => (
                  <th
                    key={d.fecha}
                    className={`px-0.5 py-2 text-center font-semibold ${d.es_hoy ? "bg-[#2DD4BF]/10 text-[#2DD4BF]" : d.domingo ? "text-gray-600" : "text-gray-400"}`}
                  >
                    <span className="block font-mono-num text-[11px]">{d.num}</span>
                    <span className="block text-[9px] font-medium">{d.letra}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grupos.map(([localIdGrupo, g]) => (
                <GrupoLocalCal key={localIdGrupo} nombre={g.nombre} filas={g.filas} dias={grilla?.dias || []} onCelda={tocarCelda} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/8 px-4 py-3">
          {LEYENDA.map(([label, cls]) => (
            <span key={label} className="flex items-center gap-1.5 text-[11px] text-gray-400">
              <span className={`h-3.5 w-3.5 rounded ${cls}`} /> {label}
            </span>
          ))}
          <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
            <span className="h-3.5 w-3.5 rounded" style={RAYADO} /> Fuera del alta
          </span>
        </div>
      </Card>
      <p className="mt-3 text-xs text-gray-500">
        Tocá una celda: si tiene vacaciones o una licencia, se abre; si no, se cargan desde ese día.
      </p>

      <div className="mb-4 mt-10 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-white">Feriados</h2>
          <p className="mt-1 text-[15px] text-gray-400">
            Los feriados y los días en que el local cierra. Valen para todos los locales: ese día, quien no viene no tiene falta.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={anio} onChange={(e) => setAnio(e.target.value)} className={inputCls} data-testid="filtro-anio-feriados">
            {[Number(hoyISOBA().slice(0, 4)) - 1, Number(hoyISOBA().slice(0, 4)), Number(hoyISOBA().slice(0, 4)) + 1].map((a) => (
              <option key={a} value={String(a)}>{a}</option>
            ))}
          </select>
          <button className={`${btnPrim} flex items-center gap-2`} onClick={() => setModal("feriado")} data-testid="btn-agregar-feriado">
            <Plus size={15} /> Agregar
          </button>
        </div>
      </div>

      <Card
        testid="card-feriados"
        titulo={`Cargados en ${anio}`}
        subtitulo="Los ve y los carga también la liquidación. Cada cambio queda en el historial."
        bodyCls="p-0"
      >
        <div className="divide-y divide-white/5">
          {(feriados || []).length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-gray-500">No hay feriados cargados en {anio}</p>
          )}
          {feriados?.map((f) => (
            <div key={f.id} className="flex items-center gap-4 px-5 py-3.5" data-testid={`feriado-${f.id.slice(0, 8)}`}>
              <span className="font-mono-num text-sm font-semibold text-white">{fmtFecha(f.fecha)}</span>
              <span className="flex-1 text-sm text-gray-300">{f.descripcion}</span>
              <Badge color={f.local_id ? "azul" : "gris"}>{f.local_nombre}</Badge>
              <button
                title="Eliminar feriado"
                onClick={() => setBorrarFeriado(f)}
                className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white/5 hover:text-red-300"
                data-testid={`borrar-feriado-${f.id.slice(0, 8)}`}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </Card>

      {modal === "feriado" && (
        <ModalFeriado locales={locales} onClose={() => setModal(null)} onOk={() => { setModal(null); invalidar(); }} />
      )}
      {modal === "vacaciones" && (
        <ModalVacaciones empleados={empleados} inicial={inicial} onClose={() => setModal(null)} onOk={() => { setModal(null); invalidar(); }} />
      )}
      {modal === "licencia" && (
        <ModalLicencia empleados={empleados} inicial={inicial} onClose={() => setModal(null)} onOk={() => { setModal(null); invalidar(); }} />
      )}

      {celdaSel && (
        <Modal
          titulo={`${fmtFecha(celdaSel.fecha)} — ${celdaSel.empleado.apellido}, ${celdaSel.empleado.nombre}`}
          onClose={() => setCeldaSel(null)}
          testid="modal-celda"
        >
          <p className="mb-4 text-sm text-gray-400">Este día no tiene vacaciones ni licencia. ¿Qué querés cargar desde esta fecha?</p>
          <div className="flex gap-2">
            <button
              className={btnSec}
              onClick={() => { setInicial({ empleado_id: celdaSel.empleado.id, fecha: celdaSel.fecha }); setCeldaSel(null); setModal("vacaciones"); }}
              data-testid="celda-vacaciones"
            >
              <Palmtree size={14} className="mr-1.5 inline" /> Vacaciones
            </button>
            <button
              className={btnPrim}
              onClick={() => { setInicial({ empleado_id: celdaSel.empleado.id, fecha: celdaSel.fecha }); setCeldaSel(null); setModal("licencia"); }}
              data-testid="celda-licencia"
            >
              <Stethoscope size={14} className="mr-1.5 inline" /> Licencia
            </button>
          </div>
        </Modal>
      )}

      {eventoSel && (
        <Modal
          titulo={eventoSel.evento.tipo === "vacaciones" ? "Vacaciones" : "Licencia"}
          onClose={() => setEventoSel(null)}
          testid="modal-evento"
        >
          <p className="text-sm font-semibold text-white">{eventoSel.empleado.apellido}, {eventoSel.empleado.nombre}</p>
          <p className="mt-1 font-mono-num text-sm text-[#2DD4BF]">
            {fmtFecha(eventoSel.evento.fecha_desde)} → {fmtFecha(eventoSel.evento.fecha_hasta)}
          </p>
          <p className="mt-3 text-sm text-gray-400">{eventoSel.evento.descripcion}</p>
        </Modal>
      )}

      {borrarFeriado && (
        <Modal titulo="Eliminar feriado" onClose={() => setBorrarFeriado(null)} testid="modal-borrar-feriado">
          <p className="mb-1 text-sm text-gray-300">
            <b className="text-white">{fmtFecha(borrarFeriado.fecha)}</b> — {borrarFeriado.descripcion}
          </p>
          <p className="mb-4 text-sm text-gray-400">
            Las jornadas de ese día se recalculan. El cambio queda en el historial.
          </p>
          <div className="flex justify-end gap-2">
            <button className={btnSec} onClick={() => setBorrarFeriado(null)}>Cancelar</button>
            <button
              className="rounded-xl bg-red-400 px-4 py-2 text-sm font-bold text-black transition-all hover:brightness-110 disabled:opacity-40"
              disabled={mutBorrarFeriado.isPending}
              onClick={() => mutBorrarFeriado.mutate(borrarFeriado.id)}
              data-testid="confirmar-borrar-feriado"
            >
              Eliminar
            </button>
          </div>
        </Modal>
      )}
    </motion.div>
  );
}

function GrupoLocalCal({ nombre, filas, dias, onCelda }) {
  return (
    <>
      <tr className="border-b border-white/8 bg-white/[0.03]">
        <td colSpan={dias.length + 1} className="sticky left-0 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">
          {nombre} · {filas.length} {filas.length === 1 ? "persona" : "personas"}
        </td>
      </tr>
      {filas.map((e) => (
        <tr key={e.id} className="border-b border-white/5 last:border-0" data-testid={`cal-fila-${e.legajo}`}>
          <td className="sticky left-0 z-10 min-w-44 bg-[#151A21] px-4 py-2">
            <span className="block text-[13px] font-semibold text-white">{e.apellido}, {e.nombre}</span>
            <span className="block font-mono-num text-[10px] text-gray-500">Legajo {e.legajo}</span>
          </td>
          {dias.map((d) => {
            const code = e.dias[d.fecha] || "sin_dato";
            return (
              <td key={d.fecha} className={`px-0.5 py-1 text-center ${d.es_hoy ? "bg-[#2DD4BF]/10" : ""}`}>
                <button
                  onClick={() => onCelda(e, d.fecha)}
                  title={`${fmtFecha(d.fecha)}`}
                  className={`mx-auto flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-bold transition-transform hover:scale-110 ${CELDA[code]}`}
                  style={code === "fuera_alta" ? RAYADO : undefined}
                  data-testid={`celda-${e.legajo}-${d.fecha}`}
                >
                  {LETRA[code] || ""}
                </button>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

function ModalFeriado({ locales, onClose, onOk }) {
  const [fecha, setFecha] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [localId, setLocalId] = useState("");
  const mut = useMutation({
    mutationFn: async () => api.post("/rrhh/calendario/feriado", { fecha, descripcion, local_id: localId || null }),
    onSuccess: () => { toast.success("Feriado cargado — se marcaron las jornadas"); onOk(); },
    onError: (e) => toast.error(fmtApiError(e)),
  });
  return (
    <Modal titulo="Cargar feriado / día no laborable" onClose={onClose} testid="modal-feriado">
      <div className="space-y-3.5">
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Fecha</label>
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={`${inputCls} w-full`} data-testid="feriado-fecha" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Descripción</label>
          <input value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="ej. Día de la Soberanía" className={`${inputCls} w-full`} data-testid="feriado-descripcion" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Alcance</label>
          <select value={localId} onChange={(e) => setLocalId(e.target.value)} className={`${inputCls} w-full`} data-testid="feriado-alcance">
            <option value="">Todos los locales</option>
            {locales?.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={btnSec} onClick={onClose}>Cancelar</button>
        <button className={btnPrim} disabled={mut.isPending || !fecha} onClick={() => mut.mutate()} data-testid="feriado-guardar">Guardar</button>
      </div>
    </Modal>
  );
}

function ModalVacaciones({ empleados, inicial, onClose, onOk }) {
  const [empleadoId, setEmpleadoId] = useState(inicial?.empleado_id || "");
  const [inicio, setInicio] = useState(inicial?.fecha || "");
  const [dias, setDias] = useState(7);
  const qc = useQueryClient();
  const { data: eventos } = useQuery({
    queryKey: ["calendario", inicio?.slice(0, 7) || hoyISOBA().slice(0, 7)],
    queryFn: async () => (await api.get("/rrhh/calendario", { params: { mes: inicio?.slice(0, 7) || hoyISOBA().slice(0, 7) } })).data,
  });

  const preview = useMemo(() => {
    if (!inicio) return null;
    const d0 = new Date(inicio + "T12:00:00");
    if (d0.getDay() !== 1) return { error: "La fecha de inicio tiene que ser un lunes" };
    const emp = empleados?.find((e) => e.id === empleadoId);
    const feriados = new Set(
      (eventos || []).filter((ev) => ev.tipo === "feriado" && (!ev.local_id || !emp || ev.local_id === emp.local_habitual_id))
        .map((ev) => ev.fecha_desde)
    );
    const hasta = new Date(d0);
    hasta.setDate(hasta.getDate() + dias - 1);
    const vuelta = new Date(hasta);
    vuelta.setDate(vuelta.getDate() + 1);
    while (vuelta.getDay() === 0 || feriados.has(iso(vuelta))) vuelta.setDate(vuelta.getDate() + 1);
    return { hasta, vuelta };
  }, [inicio, dias, empleadoId, empleados, eventos]);

  const mut = useMutation({
    mutationFn: async () => api.post("/rrhh/calendario/vacaciones", { empleado_id: empleadoId, fecha_desde: inicio, dias }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["empleados"] });
      toast.success(`Vacaciones cargadas — vuelve el ${fmtFecha(r.data.fecha_vuelta)}`);
      onOk();
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  return (
    <Modal titulo="Cargar vacaciones" onClose={onClose} testid="modal-vacaciones">
      <div className="space-y-3.5">
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Empleado</label>
          <select value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)} className={`${inputCls} w-full`} data-testid="vac-empleado">
            <option value="">Elegí el empleado…</option>
            {empleados?.filter((e) => e.activo).map((e) => <option key={e.id} value={e.id}>{e.apellido}, {e.nombre} ({e.legajo})</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Inicio (un lunes)</label>
            <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className={`${inputCls} w-full`} data-testid="vac-inicio" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Bloque</label>
            <select value={dias} onChange={(e) => setDias(Number(e.target.value))} className={`${inputCls} w-full`} data-testid="vac-dias">
              {[7, 14, 21, 28].map((d) => <option key={d} value={d}>{d} días corridos</option>)}
            </select>
          </div>
        </div>
        {preview?.error && <p className="rounded-xl border border-red-400/40 bg-red-400/10 px-3 py-2 text-xs text-red-300" data-testid="vac-error">{preview.error}</p>}
        {preview && !preview.error && (
          <p className="rounded-xl border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 px-3 py-2 text-sm text-[#2DD4BF]" data-testid="vac-preview">
            Del {fmtFecha(iso(new Date(inicio + "T12:00:00")))} al {fmtFecha(iso(preview.hasta))} — <b>vuelve el {fmtFecha(iso(preview.vuelta))}</b>
          </p>
        )}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={btnSec} onClick={onClose}>Cancelar</button>
        <button
          className={btnPrim}
          disabled={mut.isPending || !empleadoId || !inicio || !!preview?.error}
          onClick={() => mut.mutate()}
          data-testid="vac-guardar"
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}

function ModalLicencia({ empleados, inicial, onClose, onOk }) {
  const [empleadoId, setEmpleadoId] = useState(inicial?.empleado_id || "");
  const [subtipo, setSubtipo] = useState("enfermedad");
  const [dias, setDias] = useState("");
  const [desde, setDesde] = useState(inicial?.fecha || "");
  const [descripcion, setDescripcion] = useState("");
  const [archivo, setArchivo] = useState(null);
  const fileRef = useRef(null);

  const lic = LICENCIAS.find((l) => l.id === subtipo);
  const cambiarSubtipo = (id) => {
    setSubtipo(id);
    const l = LICENCIAS.find((x) => x.id === id);
    setDias(l.dias != null ? String(l.dias) : "");
  };

  const mut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("empleado_id", empleadoId);
      fd.append("subtipo", subtipo);
      fd.append("fecha_desde", desde);
      fd.append("dias", String(dias));
      fd.append("descripcion", descripcion);
      fd.append("archivo", archivo);
      return api.post("/rrhh/calendario/licencia", fd);
    },
    onSuccess: (r) => { toast.success(`Licencia cargada — hasta el ${fmtFecha(r.data.fecha_hasta)}`); onOk(); },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  return (
    <Modal titulo="Cargar licencia" onClose={onClose} testid="modal-licencia">
      <div className="space-y-3.5">
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Empleado</label>
          <select value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)} className={`${inputCls} w-full`} data-testid="lic-empleado">
            <option value="">Elegí el empleado…</option>
            {empleados?.filter((e) => e.activo).map((e) => <option key={e.id} value={e.id}>{e.apellido}, {e.nombre} ({e.legajo})</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Tipo de licencia</label>
            <select value={subtipo} onChange={(e) => cambiarSubtipo(e.target.value)} className={`${inputCls} w-full`} data-testid="lic-subtipo">
              {LICENCIAS.map((l) => <option key={l.id} value={l.id}>{l.label}{l.ayuda ? ` (${l.ayuda})` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Días de convenio</label>
            <input
              type="number" min="1" max="120" value={dias}
              onChange={(e) => setDias(e.target.value)}
              placeholder={lic?.dias == null ? "según certificado" : ""}
              className={`${inputCls} w-full`} data-testid="lic-dias"
            />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Desde</label>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className={`${inputCls} w-full`} data-testid="lic-desde" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Descripción (opcional)</label>
          <input value={descripcion} onChange={(e) => setDescripcion(e.target.value)} className={`${inputCls} w-full`} data-testid="lic-descripcion" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Comprobante (obligatorio)</label>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={(e) => setArchivo(e.target.files?.[0] || null)} className="hidden" />
          <button className={`${btnSec} w-full`} onClick={() => fileRef.current?.click()} data-testid="lic-archivo">
            <Paperclip size={14} className="mr-1.5 inline" /> {archivo ? archivo.name : "Adjuntar certificado (foto o PDF)"}
          </button>
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={btnSec} onClick={onClose}>Cancelar</button>
        <button
          className={btnPrim}
          disabled={mut.isPending || !empleadoId || !desde || !dias || Number(dias) < 1 || !archivo}
          onClick={() => mut.mutate()}
          data-testid="lic-guardar"
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}
